/**
 * Composite Analyzer: EMA Proximity Intelligence — Day Scope
 *
 * Day-scope adaptation of emaProximityIntelligence. The full setup-scope
 * pattern (per-EMA WR profile) needs more trades than a single day to
 * separate signal from noise; here we focus on the ONE day-level question
 * that matters every session:
 *
 *   How disciplined was the entry technique today? Were trades pullbacks
 *   to a structural level, or were you chasing extended price?
 *
 * Surfaces a WARNING when the day's chase-rate is meaningfully high (≥50%
 * of trades classified as extended) AND there are enough trades to call
 * it a pattern instead of a one-off. Emits a SUCCESS when the trader
 * stayed disciplined (mostly pullbacks) on a high-volume day, since
 * positive reinforcement is part of coaching too.
 *
 * Reuses the core helper (`classifyEmaProximity`) from
 * emaProximityIntelligence so the chase/pullback bucket boundaries match
 * setup-scope analysis exactly.
 *
 * Reads:
 *   day.trades[] (from DayContext) — each with barAnalysis.emasAtEntry,
 *   entry, stopLossPoints, netPnl
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import {
  classifyEmaProximity,
  PULLBACK_NEAR,
  CHASE_THRESHOLD,
} from './emaProximityIntelligence.js';

export const key = 'emaProximityDay';
export const tier = 'free';

// Sample-size guard. Below this count the chase-rate is too noisy to be
// useful as a coaching signal — one bad day might be 1 of 2 trades.
const DAY_MIN_CLASSIFIED = 4;
// Chase-rate threshold for the WARNING observation.
const CHASE_RATE_WARN_AT = 0.50;
// Pullback-rate threshold for the SUCCESS observation. Higher bar than
// the WARNING because we don't want to over-celebrate routine days.
const PULLBACK_RATE_PRAISE_AT = 0.75;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const dateLabel = (day) => day?.date || day?.dateStr || 'today';

export const analyze = (ctx) => {
  const { day } = ctx || {};
  if (!day) return null;
  const rawTrades = Array.isArray(day.trades) && day.trades.length > 0 ? day.trades : day.enriched;
  if (!Array.isArray(rawTrades) || rawTrades.length === 0) return null;

  // Live-trade gate: chase-rate / pullback-rate observations bucket trades by
  // outcome (WR per cohort) and emit a "today's technique was X" claim.
  // Open trades have unrealized P&L which isn't a realized outcome — including
  // them fires premature "today's discipline was bad" claims while the user
  // is still in a trade (panic-exit risk). Matches summarizePnl semantics.
  const trades = rawTrades.filter(t => {
    // enriched form: { trade, netPnl }; raw trade form: { isOpen, ... }
    const candidate = t?.trade || t;
    return candidate?.isOpen !== true;
  });
  if (trades.length === 0) return null;

  let nearCount = 0;
  let extendedCount = 0;
  let classifiedCount = 0;
  const nearTrades = [];
  const extendedTrades = [];
  for (const t of trades) {
    const p = classifyEmaProximity(t);
    if (!p) continue;
    classifiedCount++;
    if (p.extended) { extendedCount++; extendedTrades.push(t); }
    else if (p.closestDistStops <= PULLBACK_NEAR) { nearCount++; nearTrades.push(t); }
  }
  if (classifiedCount < DAY_MIN_CLASSIFIED) return null;

  const chaseRate    = extendedCount / classifiedCount;
  const pullbackRate = nearCount / classifiedCount;

  // ── WARNING: high chase rate ────────────────────────────────────────
  if (chaseRate >= CHASE_RATE_WARN_AT) {
    const wins = (group) => group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
    const extWr = extendedCount > 0 ? wins(extendedTrades) / extendedCount : 0;
    const nearWr = nearCount > 0 ? wins(nearTrades) / nearCount : null;
    const wrPart = nearWr != null
      ? ` Near-EMA entries on the same day: ${fmtPct(nearWr)} WR (${nearCount} trades).`
      : '';

    return makeObservation({
      id: `${key}:${dateLabel(day)}:chase-day`,
      analyzer: key,
      scope: 'day',
      scopeId: dateLabel(day),
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Today's entry technique was mostly chases — ${extendedCount}/${classifiedCount} extended (${fmtPct(chaseRate)})`,
      detail: `Trades entered ≥${CHASE_THRESHOLD.toFixed(1)} stops from the nearest EMA: ${extendedCount}/${classifiedCount} of today's trades, ${fmtPct(extWr)} WR.${wrPart} The day's risk was paid mostly chasing price, not pulling back to a level.`,
      action: `Tomorrow: require entries to be within ${PULLBACK_NEAR.toFixed(1)} stops of the nearest EMA. If price is too far past your trigger, the trade is something different — wait for the pullback or skip it.`,
      evidence: {
        nearCount, extendedCount, classifiedCount,
        chaseRate, pullbackRate, extendedWr: extWr, nearWr,
      },
      confidence: 0.78,
      priority: 78,
    });
  }

  // ── SUCCESS: disciplined day ────────────────────────────────────────
  if (pullbackRate >= PULLBACK_RATE_PRAISE_AT && classifiedCount >= DAY_MIN_CLASSIFIED + 2) {
    const wins = (group) => group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
    const nearWr = nearCount > 0 ? wins(nearTrades) / nearCount : 0;
    return makeObservation({
      id: `${key}:${dateLabel(day)}:disciplined-day`,
      analyzer: key,
      scope: 'day',
      scopeId: dateLabel(day),
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.EXECUTION,
      title: `Today's entry technique was disciplined — ${nearCount}/${classifiedCount} pullbacks (${fmtPct(pullbackRate)})`,
      detail: `${nearCount} of today's ${classifiedCount} entries were within ${PULLBACK_NEAR.toFixed(1)} stops of an EMA. Pullback-entry WR: ${fmtPct(nearWr)}. The day's setup was structural, not reactive.`,
      action: `Pin the rule that fired today: "entry must be within ${PULLBACK_NEAR.toFixed(1)} stops of nearest EMA." Today's quality is repeatable IF you hold the standard.`,
      evidence: {
        nearCount, extendedCount, classifiedCount,
        chaseRate, pullbackRate, nearWr,
      },
      confidence: 0.75,
      priority: 70,
    });
  }
  return null;
};
